<template>
    <access-denied v-if="accessDenied" title="conta-corrente" />
    <div id="main" v-else>
        <Loader v-if="isLoading" />
        <div class="page">
            <AlertContainer :messages="msgs" />

            <div class="vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t("adicionar-conta-corrente") }}
                        </h1>
                    </div>
                    <div class="header-buttons">
                        <button
                            class="delete-btn"
                            type="button"
                            tag="button"
                            @click="showConfirmModal"
                        >
                            {{ $t("voltar") }}
                        </button>
                        <button
                            v-if="permissao.create && !integracao"
                            class="save-btn"
                            type="button"
                            @click="save"
                            :disabled="isSaving"
                        >
                            {{ $t("salvar") }}
                            <button-loader :isSaving="isSaving" />
                        </button>
                    </div>
                </div>
                <br />
                <div class="form-columns-fit">
                    <div class="columns is-flex-wrap">
                        <div class="column is-6">
                            <vss-select
                                ref="empresaSelected"
                                required
                                searchable
                                clearable
                                title="empresa"
                                v-model="empresaSelected"
                                :invalid="isNull(empresaSelected)"
                                :options="empresaList"
                                :loading="empresaLoading"
                                @search="carregarEmpresa"
                                :disabled="isSaving"
                                placeholder="selecione-uma-opcao"
                            />
                        </div>
                        <div class="column is-6">
                            <vss-select
                                required
                                searchable
                                clearable
                                ref="colaboradorSelected"
                                title="colaboradores"
                                v-model="colaboradorSelected"
                                :invalid="isNull(colaboradorSelected)"
                                :options="colaboradorList"
                                :loading="colaboradorLoading"
                                @search="
                                    carregarColaborador(
                                        $event,
                                        empresaSelected ? empresaSelected.id : 0
                                    )
                                "
                                :disabled="isSaving || isNull(empresaSelected)"
                                placeholder="selecione-uma-opcao"
                            />
                        </div>
                        <div class="column is-6">
                            <v-money
                                ref="cotaInput"
                                v-model="contaFlex.cota"
                                title="cota"
                                tooltipLabel="tooltip-flex-cota"
                                placeholder="placeholder-preencha"
                                suffix="%"
                                :max="100"
                                :min="0"
                                :tooltipMultilined="true"
                                tooltipPosition="is-top"
                                tooltipSize="is-small"
                                :disabled="isSaving"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(contaFlex.cota)"
                                required
                            />
                        </div>
                        <div class="column is-6">
                            <vss-select
                                ref="motivoSelected"
                                required
                                title="motivo-lancamento"
                                tooltipLabel="tooltip-flex-motivo-lancamento"
                                v-model="motivoSelected"
                                :invalid="isNull(motivoSelected)"
                                :options="motivoList"
                                :loading="motivoLoading"
                                @search="carregarMotivo"
                                :disabled="true"
                                :placeholder="
                                    motivoSelected
                                        ? 'selecione-uma-opcao'
                                        : 'aviso-motivo-criacao'
                                "
                            />
                        </div>
                        <div class="column is-6">
                            <vss-select
                                ref="regraTrabalhoInput"
                                required
                                title="regra-trabalho"
                                tooltipLabel="tooltip-flex-regra-trabalho"
                                v-model="regraTrabalhoSelected"
                                :invalid="isNull(regraTrabalhoSelected)"
                                :options="regraTrabalhoList"
                                :disabled="isSaving"
                                mlLabels
                                placeholder="selecione-uma-opcao"
                            />
                        </div>
                        <div
                            v-if="
                                regraTrabalhoSelected
                                    ? regraTrabalhoSelected.id === 0
                                    : false
                            "
                            class="column is-6"
                        >
                            <vss-select
                                ref="operacaoInput"
                                required
                                title="operacao"
                                tooltipLabel="tooltip-flex-operacao"
                                v-model="operacaoSelected"
                                :invalid="isNull(operacaoSelected)"
                                :options="operacaoList"
                                :disabled="isSaving"
                                mlLabels
                                placeholder="selecione-uma-opcao"
                            />
                        </div>
                        <div class="column is-6">
                            <v-money
                                ref="valorMinimoInput"
                                required
                                title="valor-minimo"
                                tooltipLabel="tooltip-flex-valor-minimo"
                                msg="msg-campo-obrigatorio"
                                v-model="contaFlex.valorMinimo"
                                :invalid="isNull(contaFlex.valorMinimo)"
                                :max="9999999"
                                :min="-9999999"
                                :disabled="isSaving"
                            />
                        </div>
                        <div class="column is-6">
                            <v-money
                                ref="valorInput"
                                required
                                title="valor"
                                tooltipLabel="tooltip-flex-valor"
                                msg="msg-campo-obrigatorio"
                                v-model="contaFlex.flexLancamento.saldoGerado"
                                :invalid="
                                    isNull(contaFlex.flexLancamento.saldoGerado)
                                "
                                :max="9999999"
                                :min="-9999999"
                                :disabled="isSaving"
                            />
                        </div>
                        <div
                            class="column is-6"
                            v-show="
                                motivoSelected
                                    ? motivoSelected.habilitaobrigaobservacao ==
                                          1 ||
                                      motivoSelected.habilitaobrigaobservacao ==
                                          2
                                    : false
                            "
                        >
                            <v-textarea
                                ref="observacaoInput"
                                v-model="contaFlex.flexLancamento.observacao"
                                title="observacao"
                                :rows="4"
                                :maxRows="6"
                                :invalid="
                                    isNull(contaFlex.flexLancamento.observacao)
                                "
                                :required="
                                    motivoSelected
                                        ? motivoSelected.habilitaobrigaobservacao ==
                                          2
                                        : false
                                "
                                :disableTransform="true"
                                :disabled="isSaving"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ColaboradorService from "@/services/ColaboradorService.js";
import EmpresaService from "@/services/vendas/empresa/EmpresaService.js";
import MotivoService from "@/services/agro/MotivoService.js";
import FlexService from "@/services/vendas/financeiro/FlexService.js";
import FlexValidation from "@/services/validations/financeiro/flex/FlexValidation.js";
import Loader from "@/components/content/loader/Loader.vue";
import AlertContainer from "@/components/shared/AlertContainer.vue";
import VerifiedDatePicker from "@/components/shared/form/VerifiedDatePicker.vue";
import VerifiedTimePicker from "@/components/shared/form/VerifiedTimePicker.vue";

export default {
    name: "FlexManage",
    components: {
        Loader,
        AlertContainer,
        VerifiedDatePicker,
        VerifiedTimePicker,
    },
    props: {
        cameFrom: {
            type: String,
            default: "route",
        },
        idFrom: {
            type: String,
            default: null,
        },
        reloadData: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            integracao:
                this.$store.state.user.tabelasERPWebServer.includes("VEN_FLEX"),
            service: new FlexService(this.$http, this.$router),
            colaboradorService: new ColaboradorService(
                this.$http,
                this.$router
            ),
            empresaService: new EmpresaService(this.$http, this.$router),
            motivoService: new MotivoService(this.$http, this.$router),
            isLoading: false,
            msgs: [],
            isSaving: false,
            validation: new FlexValidation(),
            accessDenied: false,
            empresaList: [],
            empresaSelected: null,
            empresaLoading: false,
            colaboradorList: [],
            colaboradorSelected: null,
            colaboradorLoading: false,
            motivoList: [],
            motivoSelected: null,
            motivoLoading: false,
            isChanged: false,
            contaFlex: {
                idEmpresa: null,
                idUsuario: null,
                regraTrabalho: null,
                operacao: null,
                cota: 0,
                valorMinimo: 0,
                flexLancamento: {
                    idMotivo: null,
                    saldoGerado: 0,
                    saldoLiberado: 0,
                    observacao: null,
                },
            },
            regraTrabalhoList: [
                {
                    id: 0,
                    label: "pedido-upper",
                },
                {
                    id: 1,
                    label: "retorno-pedido",
                },
                {   
                    id: 2,
                    label: "nota-fiscal",
                },
                {   
                    id: 3,
                    label: "lancamento-erp",
                }
            ],
            regraTrabalhoSelected: null,
            operacaoList: [
                {
                    id: 0,
                    label: "debita-automatico",
                },
                {
                    id: 1,
                    label: "credita-automatico",
                },
                {
                    id: 2,
                    label: "debita-credita-automatico",
                },
                {
                    id: 3,
                    label: "debita-credita-manualmente",
                },
            ],
            operacaoSelected: null,
        };
    },
    watch: {
        contaFlex: {
            handler: function () {
                this.isChanged = true
            },
            deep: true
        },
        empresaSelected(newValue) {
            const empresaSelectedIdNotNull = newValue ? newValue.id : 0;
            this.contaFlex.idEmpresa = empresaSelectedIdNotNull;
            this.colaboradorSelected = null;

            this.carregarColaborador(null, empresaSelectedIdNotNull);
        },
        colaboradorSelected(newValue) {
            const handleNullNewValue = newValue ? newValue.id : null;

            this.contaFlex.idUsuario = handleNullNewValue;
        },
        motivoSelected(newValue) {
            const handleNullNewValue = newValue ? newValue.id : null;

            this.contaFlex.flexLancamento.idMotivo = handleNullNewValue;
        },
        regraTrabalhoSelected(newValue) {
            const handleNullNewValue = newValue ? newValue.id : 0;

            this.contaFlex.regraTrabalho = handleNullNewValue;
        },
        operacaoSelected(newValue) {
            const handleNullNewValue = newValue ? newValue.id : 0;

            this.contaFlex.operacao = handleNullNewValue;
        }
    },
    created() {
        this.permissao = this.hasPermissionAccess(
            "vendas",
            "financeiro",
            "flex"
        );

        if (!this.permissao.create) {
            this.accessDenied = true;
        }
    },
    methods: {
        save() {
            this.isLoading = true;

            if (!this.validation.validate(this.contaFlex, this.$refs)) {
                this.$root.showMessage(
                    "alert",
                    "msg-campos-obrigatorios",
                    true
                );
                this.isLoading = false;
                return;
            }
            this.isSaving = true;

            this.contaFlex.flexLancamento.saldoLiberado =
                this.contaFlex.flexLancamento.saldoGerado;

            this.service
                .save(this.contaFlex)
                .then((value) => {
                    if (value.message == null) {
                        const msg = this.$t("msg-save-sucesso-n", { n: this.$t("conta-corrente") });

                        this.$root.showMessage("success", msg, true, false);
                        this.$emit("saved");
                    } else {
                        if (this.$t(value.message)) {
                            this.$root.showMessage(
                                "alert",
                                value.message,
                                true
                            );
                        } else {
                            this.$root.showMessage(
                                "error",
                                "msg-problema-realizar-acao",
                                true
                            );
                        }
                    }
                })
                .catch((e) => {
                    const responseBodyValidation =
                        e.body &&
                        !this.isNull(e.body.message) &&
                        this.$te(e.body.message);

                    if (responseBodyValidation) {
                        this.$root.showMessage(
                            "alert",
                            this.$t(e.body.message),
                            true,
                            false
                        );
                    } else {
                        this.$root.showMessage(
                            "error",
                            "msg-problema-realizar-acao",
                            true
                        );
                    }
                })
                .finally(() => {
                    this.isSaving = false;
                    this.isLoading = false;
                });
        },
        carregarEmpresa(query) {
            query = this.isNull(query) ? ".*" : this.removeToSearch(query);

            this.empresaLoading = true;
            this.empresaService
                .findAllComboSituacao(3, query)
                .then((body) => {
                    if (body.content) {
                        this.empresaList = body.content.map((m) => {
                            return {
                                id: m.idempresa,
                                label:
                                    m.replicapda == 2
                                        ? "Inativo | " +
                                          m.nome +
                                          " - " +
                                          m.rsocial
                                        : m.nome + " - " + m.rsocial,
                                replicapda: m.replicapda,
                            };
                        });
                    }
                })
                .catch((e) => {
                    console.error("Erro ao carregar empresas.");
                })
                .finally((_) => {
                    this.empresaLoading = false;
                });
        },
        carregarColaborador(query = null, idEmpresa = 0) {
            this.colaboradorLoading = true;
            query = this.isNull(query) ? ".*" : this.removeToSearch(query);

            this.colaboradorService
                .findListAssociatedEmpresaFlex(query, idEmpresa)
                .then(this.mapColaboradorList)
                .then((value) => {
                    if (value != null) {
                        this.colaboradorList = value;
                    }
                })
                .catch((e) => {
                    this.$root.showMessage("error", "msg-erro-load", true);
                })
                .finally((_) => {
                    this.colaboradorLoading = false;
                });
        },
        carregarMotivo() {
            this.motivoLoading = true;
            this.motivoService
                .findAll("motivo", "desc", 0, 10000, 1, [
                    ".*",
                    "CONTA_CORRENTE_FLEX",
                ])
                .then((response) => {
                    this.motivoList = response.content.map(
                        ({
                            id,
                            descricao: label,
                            cor,
                            icone,
                            habilitaobrigaobservacao,
                            inicio,
                        }) => ({
                            id,
                            label,
                            cor,
                            icone: icone.toLowerCase(),
                            habilitaobrigaobservacao,
                            inicio,
                        })
                    );

                    this.motivoSelected = this.motivoList.find(
                        (motivo) => motivo.inicio === 1
                    );
                })
                .catch((e) => {
                    this.$root.showMessage("error", "msg-erro-load", true);
                })
                .finally((_) => {
                    this.motivoLoading = false;
                    this.isChanged = false;
                });
        },
        mapColaboradorList(response) {
            return response.content.map((m) => {
                return {
                    id: m.id,
                    label: m.nome,
                };
            });
        },
        showConfirmModal() {
            if (this.isChanged) {
                this.$root.confirm({
                    message: this.$t('msg-tem-certeza-alteracoes'),
                    onConfirm: (_) => {
                        this.$emit('voltar', true);
                    },
                    onCancel: (_) => {
                        return;
                    },
                });
            } else {
                this.$emit('voltar', true);
            }
        },
    },
};
</script>
<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: auto;
    .page {
        position: relative;
        padding-left: 0;
    }
    .sovis-container-user {
        cursor: pointer;
        padding: 2px 10px;
        border: solid 1px $primary-color;
        background-color: #f0f0f0;
        border-radius: 6px;
        display: flex;
        .qtd-users {
            font-weight: bolder;
            font-size: 18px;
            margin: 0 10px;
            &.is-success {
                color: $green-alert;
            }
            &.is-danger {
                color: $orange;
            }
        }
    }
}
</style>
